import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArchiveRestore, Check, ChevronDown, ChevronRight, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { DiffView } from '../rfp/DiffView';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const plain = (s) => (s || '').replace(/<[^>]+>/g, '').trim();

/** Admin queue of narrative wording promoted from RFPs by non-admins — side-by-side old vs new, Keep / Revert each. */
export const SpotCheckQueue = ({ onResolved, onOpen, refreshKey }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState('');
  const [expanded, setExpanded] = useState(null);
  const load = useCallback(() => axios.get(`${API_URL}/api/rfp/narrative-library/spot-checks`, { withCredentials: true }).then((r) => { setData(r.data); setExpanded((x) => x || r.data.items[0]?.id || null); }).catch(() => setData({ items: [], count: 0 })), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const resolve = async (it, action) => {
    setBusy(it.id);
    try {
      await axios.post(`${API_URL}/api/rfp/narrative-library/${it.id}/spot-check`, { action }, { withCredentials: true });
      toast.success(action === 'keep' ? `Kept — “${it.heading}” now carries ${it.by.split('@')[0]}'s wording` : `Reverted “${it.heading}” to the previous library text`);
      await load(); onResolved?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not update'); }
    finally { setBusy(''); }
  };
  if (!data || !data.count) return null;
  return (
    <div className="border border-violet-200 bg-violet-50/40 rounded-lg p-3 mb-4" data-testid="nlib-spotcheck-queue">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 text-left" data-testid="nlib-spotcheck-queue-toggle">
        {open ? <ChevronDown className="w-4 h-4 text-violet-700" /> : <ChevronRight className="w-4 h-4 text-violet-700" />}
        <ShieldCheck className="w-4 h-4 text-violet-700" />
        <span className="text-sm font-semibold text-[#0F2040]">Spot-check queue</span>
        <Badge variant="outline" className="text-[9px] bg-violet-100 text-violet-800 border-violet-200" data-testid="nlib-spotcheck-queue-count">{data.count} awaiting</Badge>
        <span className="text-[11px] text-slate-500">Wording promoted from RFPs by the team is live already — compare old vs new and keep or revert each.</span>
      </button>
      {open && (
        <ul className="mt-3 space-y-2" data-testid="nlib-spotcheck-queue-list">
          {data.items.map((it, i) => (
            <li key={it.id} className="bg-white border border-slate-200 rounded-md" data-testid={`nlib-spotcheck-item-${i}`}>
              <div className="flex items-center gap-2 flex-wrap px-3 py-2 text-xs">
                <button type="button" onClick={() => setExpanded((x) => (x === it.id ? null : it.id))} className="text-slate-400" data-testid={`nlib-spotcheck-item-toggle-${i}`}>{expanded === it.id ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}</button>
                <button type="button" onClick={() => onOpen?.(it)} className="font-semibold text-[#0F2040] hover:underline" data-testid={`nlib-spotcheck-item-heading-${i}`}>{it.heading}</button>
                {it.section_type && <Badge variant="outline" className="text-[9px] bg-cyan-50 text-cyan-800 border-cyan-200">{it.section_type}</Badge>}
                <span className="text-slate-500">promoted by <b>{it.by}</b> from “{it.project_name}” · {it.at ? new Date(it.at).toLocaleDateString() : ''} · {it.words_before} → {it.words_after} words · used {it.uses}×</span>
                <span className="ml-auto flex items-center gap-1.5">
                  <Button size="sm" className="h-6 text-[11px] bg-[#0F2040] hover:bg-[#1A3055] text-white" disabled={busy === it.id} onClick={() => resolve(it, 'keep')} data-testid={`nlib-spotcheck-item-keep-${i}`}>{busy === it.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}Keep</Button>
                  <Button size="sm" variant="outline" className="h-6 text-[11px]" disabled={busy === it.id} onClick={() => resolve(it, 'revert')} data-testid={`nlib-spotcheck-item-revert-${i}`}><ArchiveRestore className="w-3 h-3 mr-1" />Revert</Button>
                </span>
              </div>
              {expanded === it.id && <div className="px-3 pb-3" data-testid={`nlib-spotcheck-item-diff-${i}`}><DiffView oldText={plain(it.previous_draft)} newText={plain(it.draft)} oldLabel="Previous library text" newLabel={`Promoted from “${it.project_name}”`} /></div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
